import React, { useState, useEffect } from 'react';
import { 
  LoanApplication, 
  CreateLoanApplication, 
  UpdateLoanApplication,
  applicationApi 
} from '../services/api';
import DocumentUpload from './DocumentUpload';
import './LoanApplicationForm.css';

interface LoanApplicationFormProps {
  applicationId?: string;
  onSave?: (application: LoanApplication) => void;
  onCancel?: () => void;
}

const LoanApplicationForm: React.FC<LoanApplicationFormProps> = ({
  applicationId,
  onSave,
  onCancel
}) => {
  const [application, setApplication] = useState<LoanApplication | null>(null);
  const [formData, setFormData] = useState<CreateLoanApplication>({
    first_name: '',
    middle_name: '',
    last_name: '',
    date_of_birth: '',
    ssn: '',
    applicant_email: '',
    phone_number: '',
    street_address: '',
    city: '',
    state_province: '',
    zip_code: '',
    employment_status: '',
    primary_income_source: '',
    annual_income: undefined,
    loan_amount: undefined,
    loan_term: '',
    loan_purpose: '',
    terms_accepted: false,
    notes: '',
    underwriter_note: ''
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingUnderwriterNote, setSavingUnderwriterNote] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDocuments, setShowDocuments] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);

  // Loan purpose options
  const loanPurposes = [
    'Home Purchase',
    'Home Refinance',
    'Home Equity Line of Credit',
    'Personal Loan',
    'Auto Loan',
    'Business Loan',
    'Investment Property',
    'Other'
  ];

  // Loan term options
  const loanTerms = [
    '5 years',
    '10 years',
    '15 years',
    '20 years',
    '25 years',
    '30 years'
  ];

  // Employment status options
  const employmentStatuses = [
    'Full-time Employment',
    'Part-time Employment',
    'Self-employed',
    'Contract/Freelance',
    'Unemployed',
    'Retired',
    'Student'
  ];

  // Primary income source options
  const incomeSources = [
    'Salary/Wages',
    'Business Income',
    'Investment Income',
    'Retirement/Pension',
    'Social Security',
    'Rental Income',
    'Other'
  ];

  useEffect(() => {
    if (applicationId) {
      loadApplication();
    }
    
    // Get current user from localStorage
    const userStr = localStorage.getItem('user');
    if (userStr) {
      setCurrentUser(JSON.parse(userStr));
    }
  }, [applicationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadApplication = async () => {
    if (!applicationId) return;

    try {
      setLoading(true);
      const app = await applicationApi.getApplication(applicationId);
      setApplication(app);
      setFormData({
        first_name: app.first_name || '',
        middle_name: app.middle_name || '',
        last_name: app.last_name || '',
        date_of_birth: app.date_of_birth || '',
        ssn: app.ssn || '',
        applicant_email: app.applicant_email || '',
        phone_number: app.phone_number || '',
        street_address: app.street_address || '',
        city: app.city || '',
        state_province: app.state_province || '',
        zip_code: app.zip_code || '',
        employment_status: app.employment_status || '',
        primary_income_source: app.primary_income_source || '',
        annual_income: app.annual_income || undefined,
        loan_amount: app.loan_amount || undefined,
        loan_term: app.loan_term || '',
        loan_purpose: app.loan_purpose || '',
        terms_accepted: app.terms_accepted || false,
        notes: app.notes || '',
        underwriter_note: app.underwriter_note || ''
      });
      setShowDocuments(true);
    } catch (err) {
      setError('Failed to load application');
      console.error('Error loading application:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else if (name === 'loan_amount' || name === 'annual_income') {
      setFormData(prev => ({
        ...prev,
        [name]: value ? parseFloat(value) : undefined
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.first_name?.trim() || !formData.last_name?.trim()) {
      setError('First name and last name are required');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      let savedApplication: LoanApplication;

      if (applicationId && application) {
        // Update existing application
        const updates: UpdateLoanApplication = {
          first_name: formData.first_name,
          middle_name: formData.middle_name,
          last_name: formData.last_name,
          date_of_birth: formData.date_of_birth,
          ssn: formData.ssn,
          applicant_email: formData.applicant_email,
          phone_number: formData.phone_number,
          street_address: formData.street_address,
          city: formData.city,
          state_province: formData.state_province,
          zip_code: formData.zip_code,
          employment_status: formData.employment_status,
          primary_income_source: formData.primary_income_source,
          annual_income: formData.annual_income,
          loan_amount: formData.loan_amount,
          loan_term: formData.loan_term,
          loan_purpose: formData.loan_purpose,
          terms_accepted: formData.terms_accepted,
          notes: formData.notes
        };
        savedApplication = await applicationApi.updateApplication(applicationId, updates);
      } else {
        // Create new application
        savedApplication = await applicationApi.createApplication(formData);
      }

      setApplication(savedApplication);
      setShowDocuments(true);

      if (onSave) {
        onSave(savedApplication);
      }
      
    } catch (err) {
      setError(applicationId ? 'Failed to update application' : 'Failed to create application');
      console.error('Error saving application:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitApplication = async () => {
    if (!applicationId || !application) {
      setError('Cannot submit application: No application selected');
      return;
    }

    const isResubmission = application.status === 'Submitted';
      
    try {
      setSaving(true);
      setError(null);

      // First, save any current form changes
      if (!formData.first_name?.trim() || !formData.last_name?.trim()) {
        setError('First name and last name are required');
        setSaving(false);
        return;
      }

        // Save current form data first
        const formUpdates: UpdateLoanApplication = {
          first_name: formData.first_name,
          middle_name: formData.middle_name,
          last_name: formData.last_name,
          date_of_birth: formData.date_of_birth,
          ssn: formData.ssn,
          applicant_email: formData.applicant_email,
          phone_number: formData.phone_number,
          street_address: formData.street_address,
          city: formData.city,
          state_province: formData.state_province,
          zip_code: formData.zip_code,
          employment_status: formData.employment_status,
          primary_income_source: formData.primary_income_source,
          annual_income: formData.annual_income,
          loan_amount: formData.loan_amount,
          loan_term: formData.loan_term,
          loan_purpose: formData.loan_purpose,
          terms_accepted: formData.terms_accepted,
          notes: formData.notes
        };
        
        let savedApplication = await applicationApi.updateApplication(applicationId, formUpdates);

        // Then update status to 'Submitted' and generate manifest
        const statusUpdate: UpdateLoanApplication = {
          status: 'Submitted'
        };
        
        savedApplication = await applicationApi.updateApplication(applicationId, statusUpdate);
        
        // Generate and save manifest file
        await applicationApi.generateManifest(applicationId);
        
        // Fetch the latest application data to ensure we have the most current state
        const latestApplication = await applicationApi.getApplication(applicationId);
        
        // Update local state with the latest data
        setApplication(latestApplication);
        
        // Also update the form data to reflect any changes
        setFormData(prev => ({
          ...prev,
          first_name: latestApplication.first_name || '',
          middle_name: latestApplication.middle_name || '',
          last_name: latestApplication.last_name || '',
          date_of_birth: latestApplication.date_of_birth || '',
          ssn: latestApplication.ssn || '',
          applicant_email: latestApplication.applicant_email || '',
          phone_number: latestApplication.phone_number || '',
          street_address: latestApplication.street_address || '',
          city: latestApplication.city || '',
          state_province: latestApplication.state_province || '',
          zip_code: latestApplication.zip_code || '',
          employment_status: latestApplication.employment_status || '',
          primary_income_source: latestApplication.primary_income_source || '',
          annual_income: latestApplication.annual_income,
          loan_amount: latestApplication.loan_amount,
          loan_term: latestApplication.loan_term || '',
          loan_purpose: latestApplication.loan_purpose || '',
          terms_accepted: latestApplication.terms_accepted || false,
          notes: latestApplication.notes || ''
        }));


        // Call onCancel to go back to applications list
        if (onCancel) {
          onCancel();
        }
        
      } catch (err) {
        setError('Failed to submit application');
        console.error('Error submitting application:', err);
      } finally {
        setSaving(false);
      }
  };

  const handleSaveUnderwriterNote = async () => {
    if (!applicationId) return;
    
    try {
      setSavingUnderwriterNote(true);
      setError(null);
      
      await applicationApi.updateUnderwriterNote(applicationId, formData.underwriter_note || '');
      
      // Reload application to get the updated note
      await loadApplication();
      
    } catch (err) {
      setError('Failed to save underwriter note');
      console.error('Error saving underwriter note:', err);
    } finally {
      setSavingUnderwriterNote(false);
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return <div className="loading">Loading application...</div>;
  }

  return (
    <div className="loan-application-form">
      <div className="form-header">
        <div className="header-top">
          <button
            type="button"
            onClick={onCancel}
            className="btn btn-sm btn-secondary back-button"
            title="Back to Applications List"
          >
            ← 
          </button>
          <h2>{applicationId ? 'Edit Loan Application' : 'New Loan Application'}</h2>
        </div>
        {application && (
          <div className="application-info">
            <span className="app-id">ID: {application.application_id}</span>
            <span className={`status status-${application.status.toLowerCase().replace(' ', '-')}`}>
              Status: {application.status.toUpperCase()}
            </span>
          </div>
        )}
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="application-form">
        <div className="form-section">
          <h3>Personal Details</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="first_name">
                First Name <span className="required">*</span>
              </label>
              <input
                id="first_name"
                name="first_name"
                type="text"
                value={formData.first_name}
                onChange={handleInputChange}
                placeholder="John"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="middle_name">Middle Name (Optional)</label>
              <input
                id="middle_name"
                name="middle_name"
                type="text"
                value={formData.middle_name}
                onChange={handleInputChange}
                placeholder="Doe"
              />
            </div>

            <div className="form-group">
              <label htmlFor="last_name">
                Last Name <span className="required">*</span>
              </label>
              <input
                id="last_name"
                name="last_name"
                type="text"
                value={formData.last_name}
                onChange={handleInputChange}
                placeholder="Smith"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="date_of_birth">Date of Birth</label>
              <input
                id="date_of_birth"
                name="date_of_birth"
                type="date"
                value={formData.date_of_birth}
                onChange={handleInputChange}
                placeholder="Select your date of birth"
              />
            </div>

            <div className="form-group">
              <label htmlFor="ssn">Social Security Number / Social Insurance Number</label>
              <input
                id="ssn"
                name="ssn"
                type="text"
                value={formData.ssn}
                onChange={handleInputChange}
                placeholder="XXX-XX-XXXX (US) or XXX-XXX-XXX (CA)"
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Contact Information</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="applicant_email">Email Address</label>
              <input
                id="applicant_email"
                name="applicant_email"
                type="email"
                value={formData.applicant_email}
                onChange={handleInputChange}
                placeholder="john.smith@example.com"
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone_number">Phone Number</label>
              <input
                id="phone_number"
                name="phone_number"
                type="tel"
                value={formData.phone_number}
                onChange={handleInputChange}
                placeholder="(XXX) XXX-XXXX"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group full-width">
              <label htmlFor="street_address">Street Address</label>
              <input
                id="street_address"
                name="street_address"
                type="text"
                value={formData.street_address}
                onChange={handleInputChange}
                placeholder="123 Main St"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="city">City</label>
              <input
                id="city"
                name="city"
                type="text"
                value={formData.city}
                onChange={handleInputChange}
                placeholder="Anytown"
              />
            </div>

            <div className="form-group">
              <label htmlFor="state_province">State / Province</label>
              <input
                id="state_province"
                name="state_province"
                type="text"
                value={formData.state_province}
                onChange={handleInputChange}
                placeholder="CA"
              />
            </div>

            <div className="form-group">
              <label htmlFor="zip_code">Zip / Postal Code</label>
              <input
                id="zip_code"
                name="zip_code"
                type="text"
                value={formData.zip_code}
                onChange={handleInputChange}
                placeholder="90210"
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Employment & Income Information</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="employment_status">Employment Status</label>
              <select
                id="employment_status"
                name="employment_status"
                value={formData.employment_status}
                onChange={handleInputChange}
              >
                <option value="">Select status</option>
                {employmentStatuses.map(status => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="primary_income_source">Primary Income Source</label>
              <select
                id="primary_income_source"
                name="primary_income_source"
                value={formData.primary_income_source}
                onChange={handleInputChange}
              >
                <option value="">Select source</option>
                {incomeSources.map(source => (
                  <option key={source} value={source}>
                    {source}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="annual_income">Annual Income ($)</label>
              <input
                id="annual_income"
                name="annual_income"
                type="number"
                step="0.01"
                min="0"
                value={formData.annual_income || ''}
                onChange={handleInputChange}
                placeholder="e.g., 75000"
              />
              {formData.annual_income && (
                <div className="amount-display">
                  {formatCurrency(formData.annual_income)}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Loan Request Details</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="loan_amount">Loan Amount ($)</label>
              <input
                id="loan_amount"
                name="loan_amount"
                type="number"
                step="0.01"
                min="0"
                value={formData.loan_amount || ''}
                onChange={handleInputChange}
                placeholder="e.g., 10000"
              />
              {formData.loan_amount && (
                <div className="amount-display">
                  {formatCurrency(formData.loan_amount)}
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="loan_term">Loan Term</label>
              <select
                id="loan_term"
                name="loan_term"
                value={formData.loan_term}
                onChange={handleInputChange}
              >
                <option value="">Select term</option>
                {loanTerms.map(term => (
                  <option key={term} value={term}>
                    {term}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group full-width">
              <label htmlFor="loan_purpose">Loan Purpose</label>
              <select
                id="loan_purpose"
                name="loan_purpose"
                value={formData.loan_purpose}
                onChange={handleInputChange}
              >
                <option value="">Select purpose</option>
                {loanPurposes.map(purpose => (
                  <option key={purpose} value={purpose}>
                    {purpose}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Additional Notes</h3>
          <div className="form-group">
            <label htmlFor="notes">Notes</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              placeholder="Enter any additional notes or comments"
              rows={4}
            />
          </div>
        </div>

        {currentUser?.is_admin && applicationId && (
          <div className="form-section underwriter-notes-section">
            <div className="underwriter-notes-header">
              <h3>Underwriter Notes</h3>
              <span className="admin-badge">Admin Only</span>
            </div>
            <div className="form-group">
              <label htmlFor="underwriter_note">
                Internal Notes (Visible to Admin Only)
              </label>
              <textarea
                id="underwriter_note"
                name="underwriter_note"
                value={formData.underwriter_note}
                onChange={handleInputChange}
                placeholder="Enter internal notes for underwriting purposes..."
                rows={6}
                className="underwriter-textarea"
              />
            </div>
            <div className="underwriter-note-actions">
              <button
                type="button"
                onClick={handleSaveUnderwriterNote}
                disabled={savingUnderwriterNote}
                className="btn btn-info"
              >
                {savingUnderwriterNote ? 'Saving Note...' : 'Save Underwriter Note'}
              </button>
            </div>
          </div>
        )}

        <div className="form-section">
          <div className="form-group terms-checkbox">
            <label htmlFor="terms_accepted" className="checkbox-label">
              <input
                id="terms_accepted"
                name="terms_accepted"
                type="checkbox"
                checked={formData.terms_accepted}
                onChange={handleInputChange}
              />
              <span>
                I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer">Terms and Conditions</a> and authorize a credit check.
              </span>
            </label>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="submit"
            disabled={saving}
            className="btn btn-primary"
          >
            {saving ? 'Saving...' : (applicationId ? 'Update Application' : 'Create Application')}
          </button>
          
          {applicationId && application && (
            <button
              type="button"
              onClick={handleSubmitApplication}
              disabled={saving}
              className="btn btn-success"
              style={{ marginLeft: '10px' }}
            >
              {application.status === 'Submitted' ? 'Re-Submit Application' : 'Submit Application'}
            </button>
          )}
          
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="btn btn-secondary"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      {showDocuments && application && (
        <div className="documents-section">
          <DocumentUpload applicationId={application.application_id} />
        </div>
      )}
    </div>
  );
};

export default LoanApplicationForm;